import { useContext } from 'react';
import AuthContext from './auth/Context';

/**
 * useAuthGate — quyết định MỘT CHỖ DUY NHẤT: màn hình có bị chắn không, chắn bằng gì, và đã được
 * phép gọi API lấy dữ liệu chưa.
 *
 * VÌ SAO CÓ HOOK NÀY: cùng một đoạn `isPublicUser = !userInfo || email==='public'` rồi
 * `isShowingLockScreen = isPublicUser && requireLogin` đang bị chép ở AppContainer, ReportFrame và
 * DataListManager. Đoạn đó chỉ nhìn `userInfo` nên không phân biệt được "chưa đăng nhập" với
 * "đang đăng nhập / đang chọn công ty" -> màn "Yêu cầu đăng nhập" hiện đè lên cả 3 nơi trong lúc
 * người dùng đang thao tác ở hộp thoại. Sửa ở một chỗ thì cả ba nơi cùng đúng.
 *
 * @param {boolean|object} requireLogin  prop của component (object thì là props truyền cho LockScreen)
 * @param {object} [opts] { error }  lỗi của react-query — 401 cũng coi như bị chắn
 * @returns {{
 *   blocked:boolean, variant:'loading'|'login'|'choose_app'|undefined, lockProps:object,
 *   canFetch:boolean, authBusy:boolean, needsApp:boolean, isLoggedIn:boolean, isPublicUser:boolean
 * }}
 */
export default function useAuthGate(requireLogin = false, opts = {}) {
  const context = useContext(AuthContext) || {};
  const { error } = opts;

  const AUTH_PHASE = context.AUTH_PHASE || {};
  const phase = context.authPhase;
  const authBusy = !!context.authBusy;

  const isPublicUser = !context.userInfo || context.userInfo?.email === 'public';
  const isLoggedIn = context.isLoggedIn ? context.isLoggedIn() : !isPublicUser;
  // Đã đăng nhập nhưng chưa chọn công ty -> phải mời CHỌN CÔNG TY, không phải mời đăng nhập.
  const needsApp = context.needsApp ? context.needsApp() : (isLoggedIn && !context.appInfo);

  // 401 lọt tới đây nghĩa là APIs đã thử làm mới token mà vẫn hỏng -> coi như chưa đăng nhập.
  const unauthorized = error?.code === 401 || error?.response?.status === 401;

  const blocked = !!requireLogin && (authBusy || isPublicUser || needsApp || unauthorized);

  let variant;
  if (blocked) {
    // Thứ tự quan trọng: ĐANG BẬN thì chỉ hiện vòng quay, tuyệt đối không hiện ổ khoá.
    variant = authBusy ? 'loading' : (isPublicUser || unauthorized ? 'login' : 'choose_app');
  }

  // Props cho LockScreen. Sao chép (không mutate) object `requireLogin` do component cha truyền vào.
  const lockProps = { ...(typeof requireLogin === 'object' && requireLogin !== null ? requireLogin : {}) };
  if (!(context.config?.public_token && context.config?.login_with_public_user)) {
    lockProps.autoSignin = true;
    lockProps.canCancel = false;
  }
  // Không tự bật form đăng nhập khi đang có luồng xác thực chạy dở, hoặc khi chỉ còn thiếu công ty.
  if (authBusy || needsApp) lockProps.autoSignin = false;
  lockProps.variant = variant;

  // Được phép gọi API chưa?
  //  - Trang yêu cầu đăng nhập: phải có user THẬT, đã chọn công ty, và không đang trong bước xác thực.
  //    Thiếu công ty mà vẫn gọi thì request đi kèm id_app rỗng -> lỗi 400 vô nghĩa cho người dùng.
  //  - Trang công khai: chỉ cần qua giai đoạn khởi động (lúc đó token/công ty chưa sẵn sàng).
  const canFetch = requireLogin
    ? (isLoggedIn && !needsApp && !authBusy)
    : (phase !== AUTH_PHASE.BOOTING);

  return { blocked, variant, lockProps, canFetch, authBusy, needsApp, isLoggedIn, isPublicUser, phase };
}
